<template>
  <div class="flex flex-column ga-2 search-and-filters-section">
    <SearchAndActions
      :model-value="searchQuery"
      :locale="activeLocale"
      :hide-locale="hideLocale"
      :placeholder="searchPlaceholder"
      :aria-label="searchAriaLabel"
      @update:model-value="$emit('update:searchQuery', $event)"
      @search="handleSearch"
      @clear-search="clearSearch"
      @locale-changed="localeChanged"
      @cart-clicked="cartClicked"
    />
    <SingleSelect
      :options="computedSortOptions"
      prefix="Sort by: "
      :model-value="selectedSort"
      @update:model-value="$emit('update:selectedSort', $event)"
      @change="onSortSelectChange"
    />
    <div
      v-if="!hideChips && selectedChips.length"
      class="selected-filters"
      :class="`variant-${variant}`"
    >
      <div v-for="chip in selectedChips" :key="chip.key" class="selected-chip">
        <span class="chip-text" v-html="chip.text"></span>
        <button
          @click="clearFilter(chip.key)"
          class="chip-close"
          aria-label="Remove filter"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <button
        v-if="selectedChips.length > 1"
        @click="clearAllFilters"
        class="clear-all-chip"
      >
        Clear All
      </button>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { computed } from "vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import SearchAndActions from "@/components/maker-cart/SearchAndActions.vue";

const props = defineProps({
  searchQuery: {
    type: String,
    default: "",
  },
  selectedSort: {
    type: String,
    default: "top-activities",
  },
  activeLocale: {
    type: String,
    default: "US",
  },
  selectedChips: {
    type: Array,
    default: () => [],
  },
  sortOptions: {
    type: Array,
    default: () => [],
  },
  hideLocale: {
    type: Boolean,
    default: false,
  },
  hideChips: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: "",
  },
  searchAriaLabel: {
    type: String,
    default: "",
  },
  variant: {
    type: String,
    default: "green",
  },
});

const emit = defineEmits([
  "update:searchQuery",
  "update:selectedSort",
  "search",
  "clear-search",
  "locale-changed",
  "cart-clicked",
  "clear-filter",
  "clear-all-filters",
  "sort-changed",
]);

const defaultSortOptions = [
  { value: "top-activities", text: "<b><strong>Popular</strong></b>" },
  { value: "newest", text: "<b><strong>Newest</strong></b>" },
  { value: "oldest", text: "<b><strong>Oldest</strong></b>" },
  { value: "a-z", text: "<b><strong>A-Z</strong></b>" },
  { value: "z-a", text: "<b><strong>Z-A</strong></b>" },
];

const computedSortOptions = computed(() => {
  if (props.sortOptions && props.sortOptions.length > 0) {
    return props.sortOptions;
  }
  return defaultSortOptions;
});

const handleSearch = () => {
  emit("search");
};

const clearSearch = () => {
  emit("update:searchQuery", "");
  emit("clear-search");
};

const localeChanged = (locale) => {
  emit("locale-changed", locale);
};

const cartClicked = () => {
  emit("cart-clicked");
};

const onSortSelectChange = (payload) => {
  if (!payload || !payload.value) return;
  emit("update:selectedSort", payload.value);
  emit("sort-changed", payload.value);
};

const clearFilter = (filterKey) => {
  emit("clear-filter", filterKey);
};

const clearAllFilters = () => {
  emit("clear-all-filters");
};
</script>

<style scoped>
.selected-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.selected-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  height: 40px;
  background: #ffffff;
  border: 1px solid #00925e;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: #00925e;
  letter-spacing: 0.024px;
}

.chip-text {
  display: flex;
  align-items: center;
}

.chip-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.024px;
}

.chip-values {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
}

.chip-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  color: #00925e;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.2s ease;
}

.chip-close:hover {
  opacity: 0.7;
}

.clear-all-chip {
  padding: 10px 16px;
  height: 40px;
  background: #f2f3fb;
  border: none;
  border-radius: 8px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: #e00069;
  letter-spacing: 0.024px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clear-all-chip:hover {
  background: #e6e7f0;
}

/* Purple variant styles */
.variant-purple .selected-chip {
  border-color: var(--primary-purple);
  color: var(--primary-purple);
}

.variant-purple .chip-close {
  color: var(--primary-purple);
}

/* Pink variant styles */
.variant-pink .selected-chip {
  border-color: var(--pink-dark-1);
  color: var(--pink-dark-1);
}

.variant-pink .chip-close {
  color: var(--pink-dark-1);
}

@media (max-width: 768px) {
  .flex.flex-column.ga-2 {
    gap: 12px;
  }

  .selected-filters {
    gap: 6px;
  }

  .selected-chip {
    padding: 8px 12px;
    height: 36px;
    font-size: var(--fs-small);
    line-height: 20px;
  }

  .chip-close {
    width: 18px;
    height: 18px;
  }

  .clear-all-chip {
    padding: 8px 12px;
    height: 36px;
    font-size: var(--fs-small);
    line-height: 20px;
  }
}

@media (max-width: 480px) {
  .search-and-filters-section {
    display: none;
  }
}
</style>
